import { useEffect, useState } from 'react';
import { ListItem, ListItemButton, ListItemText, styled } from '@mui/material';

import { MessageBus } from '@app-velocity/message-bus';
import {
  ForwardArrowIcon,
  PrototypesIcon,
  WarningTriangleIcon,
} from '@app-velocity/icons';
import { Skeleton, Tooltip } from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';

import { useFlows } from '../../../../../../../hooks';
import { FlowStartingPoint } from '../../../../../../../types';
import { useSelectFlowContext } from '../../context/useSelectFlowContext';

export type SingleFlowProps = {
  flow: FlowStartingPoint;
};

const StyledFlowIcon = styled('div')<{ hovered: boolean }>(
  ({ theme, hovered }) => ({
    marginRight: theme.spacing(2),
    svg: {
      fill: hovered
        ? 'var(--icons-icon-primary-color)'
        : 'var(--icon-icon-tertiary-color)',
    },
  })
);

const StyledForwardArrowIcon = styled('div')<{ hovered: boolean }>(
  ({ hovered }) => ({
    svg: {
      fill: hovered
        ? 'var(--icons-icon-primary-color)'
        : 'var(--icon-icon-tertiary-color)',
    },
  })
);

const StyledWarningIcon = styled('div')({
  svg: {
    fill: 'var(--warning-warning-icon-primary-color)',
  },
});

const messageBus = new MessageBus('prototypes');

export const SingleFlow = ({ flow }: SingleFlowProps) => {
  const { configureFlow, mode, workspaceId, teamId } = useSelectFlowContext();
  const { t } = useI18n();
  const [hovered, setHovered] = useState(false);
  const { isFlowCorrectlyWrapped } = useFlows();
  const [isNodeChecking, setNodeChecking] = useState(true);
  const [isCorrectlyWrapped, setIsCorrectlyWrapped] = useState<boolean>(false);

  const verifyWrapping = async (nodeId: string) => {
    const isCorrectlyWrapped = await isFlowCorrectlyWrapped(nodeId);
    setIsCorrectlyWrapped(isCorrectlyWrapped);
    setNodeChecking(false);
  };

  useEffect(() => {
    verifyWrapping(flow.nodeId);
  }, [flow]);

  const goToScenarioConfiguration = async (nodeId: string) => {
    if (!isCorrectlyWrapped) {
      messageBus.publish('modal:incorrect-flow-structure:open', {
        nodeId,
        mode,
        workspaceId,
        teamId,
      });

      return;
    }

    configureFlow(nodeId);
  };

  if (isNodeChecking) return <Skeleton height={32} />;

  return (
    <ListItem key={flow.nodeId}>
      <ListItemButton
        onClick={() => goToScenarioConfiguration(flow.nodeId)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-ph-capture-attribute-id="flow_option"
        data-ph-capture-attribute-wrapped={isCorrectlyWrapped}
      >
        <StyledFlowIcon hovered={hovered}>
          <PrototypesIcon />
        </StyledFlowIcon>
        <ListItemText>{flow.name}</ListItemText>
        {isCorrectlyWrapped ? (
          <StyledForwardArrowIcon hovered={hovered}>
            <ForwardArrowIcon />
          </StyledForwardArrowIcon>
        ) : (
          <StyledWarningIcon>
            <Tooltip title={t('prototypes:flow_section.tooltip')}>
              <WarningTriangleIcon />
            </Tooltip>
          </StyledWarningIcon>
        )}
      </ListItemButton>
    </ListItem>
  );
};
